import { createTheme, alpha } from '@mui/material/styles';

const primary = '#2563EB';
const cyan = '#06B6D4';
const orange = '#F97316';
const ink = '#0F172A';
const muted = '#64748B';
const border = '#E2E8F0';
const surface = '#FFFFFF';
const background = '#F7F9FC';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: primary,
      light: '#60A5FA',
      dark: '#1D4ED8',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: cyan,
      light: '#67E8F9',
      dark: '#0891B2',
      contrastText: '#083344',
    },
    info: {
      main: cyan,
    },
    error: {
      main: '#E11D48',
    },
    background: {
      default: background,
      paper: surface,
    },
    success: {
      main: '#16A34A',
    },
    warning: {
      main: '#F59E0B',
    },
    text: {
      primary: ink,
      secondary: muted,
    },
    divider: border,
  },
  typography: {
    fontFamily:
      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      letterSpacing: 0,
      lineHeight: 1.15,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      letterSpacing: 0,
      lineHeight: 1.2,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 700,
      letterSpacing: 0,
      lineHeight: 1.25,
    },
    h4: {
      fontSize: '1.45rem',
      fontWeight: 700,
      letterSpacing: 0,
      lineHeight: 1.28,
    },
    h5: {
      fontSize: '1.2rem',
      fontWeight: 700,
      letterSpacing: 0,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 700,
      letterSpacing: 0,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.55,
    },
    body2: {
      lineHeight: 1.45,
    },
    button: {
      fontWeight: 650,
      textTransform: 'none',
      letterSpacing: 0,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: background,
          backgroundImage: 'linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)',
          color: ink,
        },
        '::selection': {
          backgroundColor: alpha(primary, 0.18),
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          padding: '0.5rem 1rem',
          transition:
            'background-color 160ms ease, border-color 160ms ease, color 160ms ease, box-shadow 160ms ease',
          fontWeight: 650,
          boxShadow: 'none',
        },
        contained: {
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(37, 99, 235, 0.18)',
          },
        },
        outlined: {
          borderWidth: 1,
          '&:hover': {
            borderWidth: 1,
            backgroundColor: alpha(primary, 0.06),
          },
        },
        text: {
          '&:hover': {
            backgroundColor: alpha(primary, 0.06),
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          position: 'relative',
          borderRadius: 8,
          backgroundColor: surface,
          border: `1px solid ${border}`,
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)',
          transition:
            'box-shadow 160ms ease, border-color 160ms ease, background-color 160ms ease',
          overflow: 'hidden',
          '&:hover': {
            boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
            borderColor: '#CBD5E1',
          },
          '&::after': {
            display: 'none',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(surface, 0.88),
          backdropFilter: 'blur(14px)',
          color: ink,
          boxShadow: 'none',
          borderBottom: `1px solid ${border}`,
          backgroundImage: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: surface,
          backgroundImage: 'none',
          borderRadius: 8,
          transition:
            'box-shadow 160ms ease, border-color 160ms ease, background-color 160ms ease',
        },
        elevation1: {
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)',
          border: `1px solid ${border}`,
        },
        elevation2: {
          boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
          border: `1px solid ${border}`,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          border: `1px solid ${border}`,
          boxShadow: '0 18px 60px rgba(15, 23, 42, 0.18)',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          border: `1px solid ${border}`,
          boxShadow: '0 16px 40px rgba(15, 23, 42, 0.12)',
        },
        list: {
          paddingTop: 6,
          paddingBottom: 6,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          marginInline: 6,
          minHeight: 38,
          gap: 10,
          transition: 'background-color 140ms ease, color 140ms ease',
          '&:hover': {
            backgroundColor: alpha(primary, 0.07),
          },
          '& .MuiSvgIcon-root': {
            fontSize: 19,
            color: muted,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            transition: 'box-shadow 160ms ease, border-color 160ms ease',
            backgroundColor: surface,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#CBD5E1',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#94A3B8',
              borderWidth: 1,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: primary,
              borderWidth: 1,
              boxShadow: `0 0 0 3px ${alpha(primary, 0.14)}`,
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: primary,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${border}`,
          padding: '12px 14px',
        },
        head: {
          fontWeight: 700,
          backgroundColor: '#F1F5F9',
          color: ink,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#F8FAFC',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 650,
        },
        colorPrimary: {
          backgroundColor: alpha(primary, 0.12),
          color: '#1E40AF',
        },
        colorSecondary: {
          backgroundColor: alpha(cyan, 0.14),
          color: '#155E75',
        },
        colorWarning: {
          backgroundColor: alpha(orange, 0.14),
          color: '#9A3412',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 650,
          letterSpacing: 0,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: 3,
          backgroundColor: primary,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          height: 8,
          backgroundColor: '#E2E8F0',
        },
        bar: {
          borderRadius: 999,
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        colorPrimary: {
          color: primary,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&:hover': {
            backgroundColor: '#F8FAFC',
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: border,
        },
      },
    },
  },
});

export default theme;
